import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CodebookService } from 'src/services/codebook.service';
import { ServiceProviderSubjectService } from 'src/services/service-provider-subject.service';
import { ServiceProvidersService } from 'src/services/service-providers.service';

@Component({
  selector: 'app-service-provider-subject-new-record',
  templateUrl: './service-provider-subject-new-record.component.html',
  styleUrls: ['./service-provider-subject-new-record.component.scss']
})
export class ServiceProviderSubjectNewRecordComponent implements OnInit {
  recordForm: FormGroup;
  providedServices: any[] = [];
  paymentMethods: any[] = [];
  outcomeStatuses: any[] = [];
  minRightEndDate: Date;
  minRealizationEndDate: Date;
  maxDate: Date = new Date(2999, 11, 31);
  minRealizationStartDate: Date;
  maxRealizationStartDate: Date;
  maxRealizationEndDate: Date;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public router: Router,
    public dialogRef: MatDialogRef<ServiceProviderSubjectNewRecordComponent>,
    private fb: FormBuilder,
    private codebookService: CodebookService,
    private serviceProvidersService: ServiceProvidersService,
    private serviceProviderSubjectService: ServiceProviderSubjectService
  ) {
    this.recordForm = this.fb.group({
      id: null,
      subjectId: this.data.subjectId,
      providedService: [null, [Validators.required]],
      rightGrantedDate: [null, [Validators.required]],
      realizationStartDate: [null],
      realizationEndDate: [null],
      rightEndDate: [null],
      monthlyPrice: [null],
      paymentMethod: [null],
      outcomeStatus: [null]
    }, { validators: this.dateRangeValidator() });
  }

  ngOnInit(): void {
    this.loadCodebooks();

    if (this.data.record) {
      this.patchForm(this.data.record);
    }

    this.updateDateConstraints();

    this.recordForm.valueChanges.subscribe(() => {
      this.updateDateConstraints();
    })
  }

  private loadCodebooks() {
    this.serviceProvidersService.findProvidedServicesByServiceProviderId(this.data.serviceProviderId).subscribe(res => {
      this.providedServices = res;
    });

    this.codebookService.getProvidedServicePaymentMethodCodebook().subscribe(res => {
      this.paymentMethods = res;
    });

    this.serviceProviderSubjectService.getServiceProviderSubjectRecordOutcomeStatuses().subscribe(res => {
      this.outcomeStatuses = res;
    });
  }

  private patchForm(record: any) {
    this.recordForm.patchValue({
      id: record.id,
      providedService: record.providedService,
      rightGrantedDate: record.rightGrantedDate,
      realizationStartDate: record.realizationStartDate,
      realizationEndDate: record.realizationEndDate,
      rightEndDate: record.rightEndDate,
      monthlyPrice: record.monthlyPrice,
      paymentMethod: record.paymentMethod,
      outcomeStatus: record.outcomeStatus?.code
    });
  }

  addNewRecords() {
    if (this.recordForm.invalid) {
      return;
    }

    this.dialogRef.close(this.recordForm.value);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  compareById = (a: any, b: any): boolean => {
    return a && b ? a.id === b.id : a === b;
  }

  private updateDateConstraints(): void {
    const rightGranted = this.recordForm.get('rightGrantedDate')?.value;
    const rightEnd = this.recordForm.get('rightEndDate')?.value;
    const realizationStart = this.recordForm.get('realizationStartDate')?.value;

    this.minRightEndDate = rightGranted ?? null;
    this.minRealizationEndDate = realizationStart ?? null;

    this.minRealizationStartDate = rightGranted ?? null;
    this.maxRealizationStartDate = rightEnd ?? null;
    this.maxRealizationEndDate = rightEnd ?? null;
  }

  private dateRangeValidator(): ValidatorFn {
    return(group: AbstractControl): ValidationErrors | null => {
      const rightGranted = group.get('rightGrantedDate')?.value;
      const rightEnd = group.get('rightEndDate')?.value;
      const realizationStart = group.get('realizationStartDate')?.value;
      const realizationEnd = group.get('realizationEndDate')?.value;
      
      const errors: any = {};

      if (rightGranted && rightEnd && rightEnd < rightGranted) {
        errors.rightEndBeforeGranted = true;
      }

      if (realizationStart && rightGranted && realizationStart < rightGranted) {
        errors.realizationStartBeforeGranted = true;
      }

      if (realizationStart && rightEnd && realizationStart > rightEnd) {
        errors.realizationStartAfterRightEnd = true;
      }

      if (realizationEnd && realizationStart && realizationEnd < realizationStart) {
        errors.realizationEndBeforeStart = true;
      }

      if (realizationEnd && rightEnd && realizationEnd > rightEnd) {
        errors.realizationEndAfterRightEnd = true;
      }
      
      return Object.keys(errors).length ? errors: null;
    }
  }

}
